/**
 * Contextual homepage sections (web).
 *
 * Each renderer takes live data and returns either a section or `null`. That
 * null is the whole empty-section rule: the server proposes a plan of candidate
 * sections, and anything with nothing real to show simply does not render.
 * Homepage space is too valuable to spend on "No events available".
 *
 * Where a section would be empty but a useful neighbour exists, it falls back
 * rather than vanishing — no live music tonight becomes "pubs with a stage",
 * which is only possible because venues and events are separate entities.
 */
import { Link } from "wouter";
import { ArrowRight, Clock, MapPin, Wrench } from "lucide-react";
import { Chip, RADII, ELEVATION, FOCUS_RING, TAP_TARGET, ScrollRow } from "@/components/hub";
import { cn } from "@/lib/utils";

/* ── shared chrome ────────────────────────────────────────────────────── */

export function SectionShell({
  title, subtitle, href, seeAllLabel = "See all", children, onSeeAll,
}: {
  title: string;
  subtitle?: string;
  href?: string;
  seeAllLabel?: string;
  children: React.ReactNode;
  onSeeAll?: () => void;
}) {
  return (
    <section className="mt-7 first:mt-0">
      <div className="flex items-end justify-between gap-4 mb-3">
        <div className="min-w-0">
          <h2 className="text-[17px] sm:text-lg font-bold tracking-tight text-slate-900">{title}</h2>
          {subtitle && <p className="text-[13px] text-slate-500 mt-0.5 truncate">{subtitle}</p>}
        </div>
        {href && (
          <Link
            href={href}
            onClick={onSeeAll}
            className={cn(
              "shrink-0 inline-flex items-center gap-1 text-[13px] font-semibold text-[#1e3a5f] hover:gap-2 transition-all",
              FOCUS_RING,
            )}
          >
            {seeAllLabel}
            <ArrowRight size={14} />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

/* ── food category rail ───────────────────────────────────────────────── */

/**
 * The categories the context engine says are worth promoting this hour, shown
 * only where a store in that category actually exists in the current town.
 * Otherwise "Breakfast" would link to an empty results page at 8am in a town
 * with no breakfast place.
 */
export function FoodCategoryRail({
  categories, availableCategories, onPick,
}: {
  categories: string[];
  availableCategories: Set<string>;
  onPick?: (category: string) => void;
}) {
  const usable = categories.filter((c) => availableCategories.has(c));
  if (usable.length === 0) return null;

  return (
    <ScrollRow>
      {usable.map((c) => (
        <Link
          key={c}
          href={`/order?category=${encodeURIComponent(c)}`}
          onClick={() => onPick?.(c)}
          className={cn(
            "shrink-0 px-4 py-2 bg-white border border-slate-200 text-[13px] font-semibold text-slate-800",
            "hover:border-[#1e3a5f] hover:text-[#1e3a5f] transition-colors whitespace-nowrap",
            RADII.pill, TAP_TARGET, FOCUS_RING, "inline-flex items-center",
          )}
        >
          {c}
        </Link>
      ))}
    </ScrollRow>
  );
}

/* ── venue feature discovery ──────────────────────────────────────────── */

/**
 * "Find a pub with a pool table" — the journey venue features exist to serve.
 * Only features some venue genuinely has are offered, so every chip leads to
 * at least one result.
 */
// Slugs must match `facility_definitions.slug` exactly — verified against the
// live table. A near-miss here (e.g. "live-music-stage" for "live-music")
// silently removes the chip, because the availability check below would never
// find it.
export const VENUE_FEATURE_CHIPS: Array<{ slug: string; label: string; emoji: string }> = [
  { slug: "pool-table",   label: "Pool",         emoji: "🎱" },
  { slug: "dart-board",   label: "Darts",        emoji: "🎯" },
  { slug: "sports-tv",    label: "Live sport",   emoji: "⚽" },
  { slug: "live-music",   label: "Live music",   emoji: "🎵" },
  { slug: "karaoke",      label: "Karaoke",      emoji: "🎤" },
  { slug: "jukebox",      label: "Jukebox",      emoji: "🎶" },
  { slug: "beer-garden",  label: "Beer garden",  emoji: "🌳" },
  { slug: "food-served",  label: "Food",         emoji: "🍔" },
  { slug: "dog-friendly", label: "Dog friendly", emoji: "🐕" },
  { slug: "function-room", label: "Function room", emoji: "🎉" },
];

export function VenueFeatureRail({
  availableSlugs, town, onPick,
}: {
  availableSlugs: Set<string>;
  town?: string;
  onPick?: (slug: string) => void;
}) {
  const usable = VENUE_FEATURE_CHIPS.filter((f) => availableSlugs.has(f.slug));
  if (usable.length === 0) return null;

  const base = town && town !== "All Towns" ? `/places/${encodeURIComponent(town.toLowerCase())}` : "/places";

  return (
    <ScrollRow>
      {usable.map((f) => (
        <Link
          key={f.slug}
          href={`${base}/${f.slug}`}
          onClick={() => onPick?.(f.slug)}
          className={cn(
            "shrink-0 inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200",
            "text-[13px] font-semibold text-slate-800 hover:border-[#1e3a5f] transition-colors whitespace-nowrap",
            RADII.pill, TAP_TARGET, FOCUS_RING,
          )}
        >
          <span aria-hidden="true">{f.emoji}</span>
          {f.label}
        </Link>
      ))}
    </ScrollRow>
  );
}

/* ── trades ───────────────────────────────────────────────────────────── */

/**
 * Framed as an intent, not a directory. "Find someone local" is what the
 * customer wants; "Browse Trades Directory" is what the database contains.
 */
export const TRADE_SHORTCUTS = [
  { label: "Cleaner",     category: "Cleaners" },
  { label: "Joiner",      category: "Joiners" },
  { label: "Plumber",     category: "Plumbers" },
  { label: "Electrician", category: "Electricians" },
  { label: "Man & van",   category: "Removals" },
  { label: "Roofer",      category: "Roofers" },
  { label: "Gardener",    category: "Gardeners" },
  { label: "Painter",     category: "Painters" },
];

export function TradesSection({ onPick }: { onPick?: (label: string) => void }) {
  return (
    <div className={cn("bg-white border border-slate-100 p-4", RADII.lg, ELEVATION.sm)}>
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-9 h-9 rounded-full bg-[#1e3a5f]/10 text-[#1e3a5f] grid place-items-center">
          <Wrench size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] text-slate-600">
            Tell us what you need and local trades come back to you — no phoning round.
          </p>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {TRADE_SHORTCUTS.map((t) => (
              <Link
                key={t.label}
                href={`/post-job?category=${encodeURIComponent(t.category)}`}
                onClick={() => onPick?.(t.label)}
                className={cn(
                  "px-3 py-1.5 bg-slate-50 border border-slate-200 text-[12px] font-semibold text-slate-700",
                  "hover:border-[#1e3a5f] hover:text-[#1e3a5f] transition-colors",
                  RADII.pill, FOCUS_RING,
                )}
              >
                {t.label}
              </Link>
            ))}
          </div>
          <Link
            href="/post-job"
            className={cn(
              "mt-3.5 inline-flex items-center justify-center gap-1.5 w-full sm:w-auto px-5 py-2.5",
              "bg-[#1e3a5f] text-white text-[14px] font-bold hover:bg-[#16304f] transition-colors",
              RADII.md, TAP_TARGET, FOCUS_RING,
            )}
          >
            Get free quotes
            <ArrowRight size={15} />
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── going out tonight ────────────────────────────────────────────────── */

export interface TonightEntry {
  id: string | number;
  title: string;
  category?: string | null;
  time?: string | null;
  venueName?: string | null;
  town?: string | null;
  href: string;
}

/**
 * Tonight's events if there are any; otherwise the venues that regularly do
 * this sort of thing. The fallback is the reason events and venues are modelled
 * separately — "nothing on tonight" is still a useful answer if it comes with
 * "but these three pubs have a stage".
 */
export function GoingOutSection({
  entries, fallbackFeatureSlugs, town, onEntry, onFeature,
}: {
  entries: TonightEntry[];
  fallbackFeatureSlugs: Set<string>;
  town?: string;
  onEntry?: (id: string | number) => void;
  onFeature?: (slug: string) => void;
}) {
  if (entries.length > 0) {
    return (
      <ScrollRow>
        {entries.map((e) => (
          <Link
            key={e.id}
            href={e.href}
            onClick={() => onEntry?.(e.id)}
            className={cn(
              "shrink-0 w-[190px] bg-white border border-slate-100 p-3.5",
              RADII.lg, ELEVATION.sm, FOCUS_RING,
              "hover:border-slate-300 transition-colors",
            )}
          >
            {e.category && <Chip tone="violet" size="xs">{e.category}</Chip>}
            <p className="text-[14px] font-bold text-slate-900 mt-2 line-clamp-2">{e.title}</p>
            {e.venueName && (
              <p className="text-[12px] text-slate-500 mt-1 truncate flex items-center gap-1">
                <MapPin size={11} className="shrink-0" />
                {e.venueName}
              </p>
            )}
            {e.time && (
              <p className="text-[12px] font-semibold text-[#1e3a5f] mt-1 flex items-center gap-1">
                <Clock size={11} />
                {e.time}
              </p>
            )}
          </Link>
        ))}
      </ScrollRow>
    );
  }

  // Nothing listed tonight — offer places instead of an empty shelf.
  const rail = <VenueFeatureRail availableSlugs={fallbackFeatureSlugs} town={town} onPick={onFeature} />;
  if (!rail) return null;
  return (
    <div>
      <p className="text-[13px] text-slate-500 mb-2.5">
        Nothing listed tonight yet — but these are always on.
      </p>
      {rail}
    </div>
  );
}
